/**
 * Who has this survey: a row of overlapping discs and the remainder as a figure. Two cuts, the
 * list card's and the editor bar's — see the `size` prop for where each one's numbers come from.
 *
 * ── The COUNT is the row's, the faces are the roster's ───────────────────────────────────────
 * `assignedClientCount` and `assignedCustomerIds` ride on `GET /survey/templates`; the names and the
 * photographs come from the roster this screen already reads, and the tab waits for both before it
 * draws a card. So this component has no wait of its own — see `useSurveysTab` — and the two can
 * still disagree: an id the roster has no row for is not drawn as a nameless disc, it is absorbed
 * into the remainder. Drawing fewer faces than the count promised leaves a little air; drawing a
 * blank disc names a client the app cannot name.
 *
 * ── It is NOT `shared/ui/AvatarChip`, and that is deliberate ─────────────────────────────────
 * `AvatarChip` is the app's one 32px avatar on the roster's skin — a `--bg-sunken` ground, a
 * `--border-2` hairline, `--text-2` ink. This disc has a theme-independent white fill, dark ink, a
 * 2px ring and an overlap, which is four declarations rather than a size, and restyling `AvatarChip`
 * through a `className` is exactly what its own header forbids.
 *
 * ── The stack is one image with one name ─────────────────────────────────────────────────────
 * `role="img"` over the whole strip, named with the people in it, because four discs read out as
 * four unlabelled graphics is noise rather than information. The card already prints „12 klientów"
 * beside it as text, so the count is never carried by this alone.
 */

import { useTranslation } from 'react-i18next';

import photoBox from '~/shared/ui/photoBox.module.css';

import type { ClientFace } from '../model/clientFace';
import styles from './AvatarStack.module.css';

/**
 * How many faces a cut draws before it starts counting.
 *
 * Four on the list card, which is what the canon draws there. FIVE on the editor's bar, because
 * that bar is the mobile app's (owner, 17 September 2026) and `ClientAvatarStack`'s `MAX_AVATARS`
 * is five.
 */
const VISIBLE: Record<AvatarStackSize, number> = { default: 4, editor: 5 };

export type AvatarStackSize = 'default' | 'editor';

interface AvatarStackProps {
  /** The ids on the survey row, in the order the backend listed them. */
  readonly customerIds: readonly number[];
  /** The backend's own figure, which may exceed the ids the roster could resolve. */
  readonly count: number;
  readonly faces: ReadonlyMap<number, ClientFace>;
  /**
   * Two stated cuts, never one averaged between them.
   *
   * `default` is the LIST card's, KANON `01-ankiety.html`: 34px discs six pixels apart, four of
   * them, on a 392px card. `editor` is the assigned bar's, and it is the MOBILE app's figure rather
   * than the web export's — 32px discs nine apart, five of them, ringed in the card colour
   * (`PersonAvatar.specs.ts` role `checkInStack`, `ClientAvatarStack`'s overlap and cap). The owner
   * refused the export's 26px cut on 17 September 2026 and named the phone's bar instead.
   *
   * A size knob rather than a second component: the decision about which faces are drawn, what the
   * remainder counts and what the one accessible name says is identical on both.
   */
  readonly size?: AvatarStackSize;
}

export function AvatarStack({ customerIds, count, faces, size = 'default' }: AvatarStackProps) {
  const { t } = useTranslation();

  const known = customerIds
    .map((id) => faces.get(id))
    .filter((face): face is ClientFace => face !== undefined);
  const shown = known.slice(0, VISIBLE[size]);
  const remainder = count - shown.length;

  if (shown.length === 0 && remainder <= 0) return null;

  const names = shown.map((face) => face.name).filter((name) => name !== '');
  const label =
    remainder > 0
      ? `${names.join(', ')} ${t('web:reports.surveys.moreClientsLabel', { count: remainder })}`
      : names.join(', ');

  return (
    <span
      className={[String(styles.stack), size === 'editor' ? String(styles.editor) : '']
        .filter(Boolean)
        .join(' ')}
      role="img"
      aria-label={label === '' ? t('web:reports.surveys.faces') : label}
    >
      {shown.map((face) =>
        face.avatarUrl === null ? (
          <span key={face.customerId} className={styles.face}>
            {face.initials}
          </span>
        ) : (
          <img
            key={face.customerId}
            className={`${String(photoBox.photo)} ${String(styles.face)}`}
            src={face.avatarUrl}
            alt=""
            loading="lazy"
            decoding="async"
          />
        ),
      )}
      {remainder > 0 ? (
        <span className={`${String(styles.face)} ${String(styles.more)}`}>
          {t('web:reports.surveys.moreClients', { count: remainder })}
        </span>
      ) : null}
    </span>
  );
}
